import type { FailCodeSectionEntity } from '../entities';

import { StsBackendOperation } from '../../sts-backend-operation';

/**
 * Parameters for fetching possible fail code sections filtered by a specific fail code.
 *
 * Used when a user selects a fail code and needs to choose which section it belongs to.
 */
export type FetchSectionsParams = {
    /** The fail code to filter sections by. */
    failCode?: string;

    /** The vehicle model code (e.g. "GUE"). */
    modelCode?: string;

    /** The vehicle model year (e.g. "2024"). */
    modelYear?: string;

    /** The vehicle option/trim code (e.g. "01"). */
    optionCode?: string;

    /** Optional abort signal for cancellation. */
    signal?: AbortSignal;
};

/**
 * Fetch possible fail code sections for a vehicle, optionally narrowed by a specific fail code.
 * Used when a user selects a fail code and needs to choose which section it belongs to.
 */
export class FetchSections extends StsBackendOperation {
    /**
     * Fetch possible sections from the API and return them as domain entities.
     *
     * @param params - the vehicle identifiers, optional fail code filter, and optional abort signal
     * @returns the matching sections as domain entities
     */
    async exec(params: FetchSectionsParams): Promise<FailCodeSectionEntity[]> {
        const { signal, ...request } = params;
        const response = await this.client.fetchPossibleSections(request, signal);

        if (!response.success) {
            return [];
        }

        return response.data.sections ?? [];
    }
}
